import {
  ACESFilmicToneMapping,
  Clock,
  Color,
  PerspectiveCamera,
  Scene,
  SRGBColorSpace,
  Vector2,
  Vector3,
  WebGLRenderer,
} from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { buildBackdrop, type Backdrop } from "./game/space";
import { currentTheme } from "./theme";

// Landscape stays at a punchy 52°. Portrait widens up to ~76° so the full
// play area (ball + target + planets) stays on-screen on phones.
function fovForAspect(aspect: number): number {
  if (aspect >= 1) return 52;
  const t = Math.min(1, (1 - aspect) / 0.55);
  return 52 + t * 24;
}

export interface SceneCtx {
  scene: Scene;
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  clock: Clock;
  container: HTMLElement;
  isMobile: boolean;
  backdrop: Backdrop;
  onFrame(cb: (t: number, dt: number) => void): () => void;
  setGodRaysStrength(multiplier: number): void;
  dispose(): void;
}

export function createScene(container: HTMLElement): SceneCtx {
  const scene = new Scene();
  // Very dark bg so the nebula dome fully takes over.
  scene.background = new Color("#020206");

  const camera = new PerspectiveCamera(
    fovForAspect(container.clientWidth / container.clientHeight),
    container.clientWidth / container.clientHeight,
    0.1,
    3000,
  );

  const renderer = new WebGLRenderer({
    antialias: true,
    alpha: false,
    powerPreference: "high-performance",
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(container.clientWidth, container.clientHeight, false);
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = ACESFilmicToneMapping;
  renderer.toneMappingExposure = currentTheme().exposure;
  container.appendChild(renderer.domElement);

  const isMobile = matchMedia("(pointer: coarse)").matches;

  const backdrop = buildBackdrop(isMobile);
  scene.add(backdrop.root);

  // ---------- post-processing ----------
  const composer = new EffectComposer(renderer);
  composer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  composer.setSize(container.clientWidth, container.clientHeight);

  const renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);

  const bloomPass = new UnrealBloomPass(
    new Vector2(container.clientWidth, container.clientHeight),
    isMobile ? 0.3 : 0.42, // strength — gentler, so halos don't stack into
                           // the radial "ring cascade" artifact on mobile
    0.6, // radius
    0.65, // threshold — only the brightest pixels bloom
  );
  composer.addPass(bloomPass);

  // God rays pass — radial sample toward the biome's virtual sun position.
  // Runs on the already-bloomed image, so only bright things radiate, which
  // gives us volumetric light shafts without a separate occlusion pipeline.
  const sunWorld = new Vector3(
    currentTheme().sunDir[0],
    currentTheme().sunDir[1],
    currentTheme().sunDir[2],
  ).normalize().multiplyScalar(900); // push far so perspective projection looks "light years away"
  const baseStrength = isMobile ? 0.16 : 0.22;
  const godRaysPass = new ShaderPass({
    uniforms: {
      tDiffuse: { value: null },
      uSunScreen: { value: new Vector2(0.5, 0.5) },
      uStrength: { value: baseStrength },
      uDecay: { value: 0.94 },
      uTint: { value: new Color(currentTheme().sunTint) },
      uAspect: { value: container.clientWidth / container.clientHeight },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform sampler2D tDiffuse;
      uniform vec2 uSunScreen;
      uniform float uStrength;
      uniform float uDecay;
      uniform vec3 uTint;
      uniform float uAspect;
      varying vec2 vUv;
      #define STEPS 28
      void main() {
        vec4 orig = texture2D(tDiffuse, vUv);
        vec2 delta = (vUv - uSunScreen) / float(STEPS);
        delta *= 0.85; // reach
        vec2 tex = vUv;
        vec3 accum = vec3(0.0);
        float weight = 1.0;
        for (int i = 0; i < STEPS; i++) {
          tex -= delta;
          vec4 s = texture2D(tDiffuse, tex);
          float lum = dot(s.rgb, vec3(0.299, 0.587, 0.114));
          // Higher threshold keeps planet halos / saturn rings from smearing
          // into the vertical ring cascade users were seeing on mobile.
          // Only very bright highlights (near the sun core) contribute now.
          float mask = smoothstep(0.85, 1.3, lum);
          accum += s.rgb * mask * weight;
          weight *= uDecay;
        }
        // Dim rays when the sun is far off-screen so we don't smear the whole frame.
        vec2 distToSun = (uSunScreen - vec2(0.5)) * vec2(uAspect, 1.0);
        float offscreen = smoothstep(1.5, 0.6, length(distToSun));
        vec3 rays = accum * uTint * uStrength * offscreen;
        gl_FragColor = vec4(orig.rgb + rays, orig.a);
      }
    `,
  });
  // God rays pass disabled for the Vibe Jam build. The radial sample
  // against bloom output produced a vertical "ring cascade" artifact on
  // mobile (bright planet halos + saturn rings smear toward the sun).
  // Keep the shader + uniforms defined so the resize tick doesn't blow
  // up, but skip registering it with the composer so rendering is clean.
  // Re-enable once we have a proper mask pass driving the radial source.
  void godRaysPass;

  // Vignette + subtle chromatic aberration for cinema vibes.
  const vignettePass = new ShaderPass({
    uniforms: {
      tDiffuse: { value: null },
      uVignette: { value: 0.7 },
      uChroma: { value: 0.0025 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform sampler2D tDiffuse;
      uniform float uVignette;
      uniform float uChroma;
      varying vec2 vUv;
      void main() {
        vec2 uv = vUv;
        vec2 dir = uv - 0.5;
        // Subtle chromatic aberration grows with distance from center.
        float d = dot(dir, dir);
        vec3 col;
        col.r = texture2D(tDiffuse, uv + dir * uChroma).r;
        col.g = texture2D(tDiffuse, uv).g;
        col.b = texture2D(tDiffuse, uv - dir * uChroma).b;
        // Soft vignette.
        float v = smoothstep(1.1, 0.25, length(dir) * 1.3);
        col *= mix(1.0, v, uVignette);
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  });
  composer.addPass(vignettePass);

  composer.addPass(new OutputPass());

  const clock = new Clock();
  const subscribers = new Set<(t: number, dt: number) => void>();

  let alive = true;
  let lastT = 0;

  function resize() {
    const w = container.clientWidth;
    const h = container.clientHeight;
    renderer.setSize(w, h, false);
    composer.setSize(w, h);
    bloomPass.resolution.set(w, h);
    camera.aspect = w / h;
    // Widen FOV on portrait so phones still see the whole playfield.
    camera.fov = fovForAspect(camera.aspect);
    camera.updateProjectionMatrix();
    (godRaysPass.uniforms.uAspect.value as number) = w / h;
    godRaysPass.uniforms.uAspect.value = w / h;
  }

  const _sunProj = new Vector3();
  function tick() {
    if (!alive) return;
    const t = clock.getElapsedTime();
    const dt = Math.min(0.1, t - lastT);
    lastT = t;
    // Park the backdrop on the camera so the skydome + distant stars
    // don't clip into the scene when the ball sits far from world
    // origin (late endless holes can stretch out thousands of units).
    backdrop.root.position.copy(camera.position);
    backdrop.update(t);
    for (const cb of subscribers) cb(t, dt);
    // Project the virtual sun to screen space so god-rays know where to pull from.
    _sunProj.copy(sunWorld).project(camera);
    (godRaysPass.uniforms.uSunScreen.value as Vector2).set(
      _sunProj.x * 0.5 + 0.5,
      _sunProj.y * 0.5 + 0.5,
    );
    composer.render();
    requestAnimationFrame(tick);
  }

  const onResize = () => resize();
  window.addEventListener("resize", onResize, { passive: true });
  const ro = new ResizeObserver(() => resize());
  ro.observe(container);

  requestAnimationFrame(tick);

  return {
    scene,
    camera,
    renderer,
    clock,
    container,
    isMobile,
    backdrop,
    onFrame(cb) {
      subscribers.add(cb);
      return () => subscribers.delete(cb);
    },
    setGodRaysStrength(multiplier: number) {
      godRaysPass.uniforms.uStrength.value = baseStrength * multiplier;
    },
    dispose() {
      alive = false;
      subscribers.clear();
      window.removeEventListener("resize", onResize);
      ro.disconnect();
      backdrop.dispose();
      composer.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
}
